import type React from 'react';
import { useRef, useState } from 'react';
import type { ArticleCarouselComponent, ArticleComponent } from 'api/types';
import Article from '../Article/Article';
import AgathaMessage from '../AgathaMessage/AgathaMessage';
import ArticleTextMessage from 'components/ArticleTextMessage';
import { useAppSelector } from 'hooks';
import { selectIsAuthorIconVisible } from 'reducers/workflows';
import { Box } from '@mui/material';

interface BaseArticleStepProps {
  isAgathaMessageVisible: boolean;
  isLastStep: boolean;
  stepIndex: number;
  /**  Component's data */
  widgetComponent: ArticleCarouselComponent;
}

const BaseArticleStep: React.FC<
  React.PropsWithChildren<BaseArticleStepProps>
> = ({ isAgathaMessageVisible, isLastStep, stepIndex, widgetComponent }) => {
  const articles = widgetComponent.component_fields.knowledge_articles;
  const ref = useRef<HTMLDivElement>(null);
  const [selectedArticleIndex, setSelectedArticleIndex] = useState<number>(0);
  const isAuthorIconVisible = useAppSelector(state =>
    selectIsAuthorIconVisible(state, stepIndex),
  );

  const readArticleAction =
    widgetComponent.component_fields.read_article_action;
  const componentText = widgetComponent.component_fields.forethought_text;
  const textOverride = widgetComponent.component_fields.text_message_override;
  const articleStepData = widgetComponent.component_fields.article_step_data;

  if (articles.length) {
    if (textOverride) {
      return (
        <ArticleTextMessage
          isLastStep={isLastStep}
          message={textOverride}
          stepIndex={stepIndex}
          widgetComponent={widgetComponent}
        />
      );
    }

    return (
      <Box>
        {isAgathaMessageVisible && (
          <AgathaMessage
            isAuthorIconVisible={isAuthorIconVisible}
            stepIndex={stepIndex}
          >
            <span dir='auto'> {componentText.agatha_message}</span>
          </AgathaMessage>
        )}
        <Box
          data-testid='article-carousel'
          sx={{ marginX: '16px', paddingX: '8px' }}
          title='article-carousel'
        >
          {articles.map((article: ArticleComponent, index: number) => {
            return (
              <Article
                article={article}
                articleIndex={index}
                articleStepData={articleStepData}
                isSelected={index === selectedArticleIndex}
                key={article.document_id}
                readArticleAction={readArticleAction}
                readArticleText={componentText.read_article_text}
                reference={index === 0 ? ref : null}
                selectArticle={() => setSelectedArticleIndex(index)}
                setBorder={articles.length > 1}
              />
            );
          })}
        </Box>
      </Box>
    );
  }

  return null;
};

export default BaseArticleStep;
